import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  CheckCircle2,
  FileText,
  Receipt,
  Wrench,
  ShieldCheck,
  LogIn,
  UserPlus,
  KeyRound,
  LifeBuoy,
} from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

export const Route = createFileRoute("/klantportaal")({
  head: () => ({
    meta: [
      { title: "Klantportaal | Elvion Group Nederland helpt u graag verder" },
      {
        name: "description",
        content:
          "Klantportaal van Elvion Group Nederland voor zakelijke en particuliere klanten. Wij helpen u graag verder — bekijk offertes, facturen, opleverrapporten en dien serviceverzoeken in.",
      },
      { property: "og:title", content: "Klantportaal | Elvion Group Nederland helpt u graag verder" },
      {
        property: "og:description",
        content:
          "Beheer offertes, facturen en installatiedocumentatie in één beveiligde omgeving. Elvion Group Nederland helpt u graag verder met service en support.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://elvion-group.nl/klantportaal" },
    ],
    links: [{ rel: "canonical", href: "https://elvion-group.nl/klantportaal" }],
  }),
  component: KlantportaalPage,
});

const features = [
  {
    icon: FileText,
    title: "Offertes & opdrachten",
    body: "Bekijk en accordeer uw offertes online en volg de status van lopende opdrachten.",
  },
  {
    icon: Receipt,
    title: "Facturen & betalingen",
    body: "Download al uw facturen en betaalherinneringen in PDF | overzichtelijk per jaar.",
  },
  {
    icon: Wrench,
    title: "Storing of service melden",
    body: "Dien direct een serviceverzoek in. Onze servicedesk pakt het binnen 1 werkdag op.",
  },
  {
    icon: ShieldCheck,
    title: "Documentatie & opleverrapporten",
    body: "NEN 1010-rapporten, garantiebewijzen en installatieschema's altijd bij de hand.",
  },
];

const promises = [
  "24/7 toegang via elk apparaat",
  "Beveiligd met 2FA",
  "Gratis voor alle klanten",
  "AVG-proof opgeslagen",
];

function KlantportaalPage() {
  return (
    <>
      <Header variant="shop" />
      <main className="bg-white">
        {/* Hero */}
        <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-orange/10 px-3 py-1 text-xs font-semibold text-orange">
                <ShieldCheck className="h-3.5 w-3.5" />
                Veilig & 24/7 beschikbaar
              </span>
              <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">
                Het ELVION klantportaal
              </h1>
              <p className="mt-4 max-w-xl text-base text-text-secondary sm:text-lg">
                Alles wat u nodig heeft op één plek: uw offertes, facturen,
                opleverrapporten en serviceverzoeken | overzichtelijk en
                veilig in uw eigen omgeving.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/auth"
                  className="inline-flex items-center gap-2 rounded-md bg-orange px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange/90"
                >
                  <LogIn className="h-4 w-4" />
                  Inloggen
                </Link>
                <Link
                  to="/word-klant"
                  className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-5 py-3 text-sm font-semibold text-foreground transition-colors hover:border-orange hover:text-orange"
                >
                  <UserPlus className="h-4 w-4" />
                  Account aanmaken
                </Link>
              </div>
              <ul className="mt-6 grid gap-2 text-sm text-text-secondary sm:grid-cols-2">
                {promises.map((p) => (
                  <li key={p} className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-orange" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-orange/20 via-orange/5 to-transparent blur-2xl" />
              <div className="relative rounded-2xl border border-border bg-card p-8 shadow-lg">
                <h2 className="font-display text-lg font-bold text-foreground">
                  Snel inloggen
                </h2>
                <p className="mt-1 text-sm text-text-secondary">
                  Log in met uw e-mailadres en wachtwoord.
                </p>
                <div className="mt-5 space-y-3">
                  <Link
                    to="/auth"
                    className="flex items-center justify-between gap-4 rounded-xl border border-border bg-background p-4 transition-colors hover:border-orange"
                  >
                    <div className="flex items-center gap-3">
                      <div className="rounded-full bg-orange/10 p-2.5 text-orange">
                        <LogIn className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">Inloggen</p>
                        <p className="text-xs text-text-secondary">Naar uw klantomgeving</p>
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-text-secondary" />
                  </Link>
                  <Link
                    to="/inloggegevens-vergeten"
                    className="flex items-center justify-between gap-4 rounded-xl border border-border bg-background p-4 transition-colors hover:border-orange"
                  >
                    <div className="flex items-center gap-3">
                      <div className="rounded-full bg-orange/10 p-2.5 text-orange">
                        <KeyRound className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">Wachtwoord vergeten</p>
                        <p className="text-xs text-text-secondary">Reset-link per mail</p>
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-text-secondary" />
                  </Link>
                  <Link
                    to="/klantenservice"
                    className="flex items-center justify-between gap-4 rounded-xl border border-border bg-background p-4 transition-colors hover:border-orange"
                  >
                    <div className="flex items-center gap-3">
                      <div className="rounded-full bg-orange/10 p-2.5 text-orange">
                        <LifeBuoy className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">Hulp nodig?</p>
                        <p className="text-xs text-text-secondary">Naar de klantenservice</p>
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-text-secondary" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="border-t border-border bg-card/30">
          <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <h2 className="font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
                Wat kunt u in het portaal?
              </h2>
              <p className="mt-3 text-base text-text-secondary">
                Het klantportaal is uw centrale plek voor alle administratie en
                service rondom uw installatie.
              </p>
            </div>

            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {features.map((f) => (
                <div
                  key={f.title}
                  className="group rounded-xl border border-border bg-background p-6 transition-all hover:border-orange hover:shadow-md"
                >
                  <div className="inline-flex rounded-lg bg-orange/10 p-3 text-orange transition-colors group-hover:bg-orange group-hover:text-white">
                    <f.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-4 font-display text-base font-bold text-foreground">
                    {f.title}
                  </h3>
                  <p className="mt-2 text-sm text-text-secondary">{f.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-border bg-card p-8 lg:p-10">
            <div className="grid items-center gap-6 lg:grid-cols-[1fr_auto]">
              <div>
                <h2 className="font-display text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">
                  Nog geen account?
                </h2>
                <p className="mt-2 max-w-2xl text-text-secondary">
                  Maak in een paar minuten een eigen account aan als
                  particulier of zakelijke klant. U krijgt direct toegang tot
                  uw documenten en service.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/word-klant"
                  className="inline-flex items-center gap-2 rounded-md bg-orange px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange/90"
                >
                  Word klant
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/klantenservice"
                  className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-5 py-3 text-sm font-semibold text-foreground transition-colors hover:border-orange hover:text-orange"
                >
                  Vragen? Klantenservice
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* SEO content */}
        <section className="border-t border-border bg-card/30">
          <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <h2 className="font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
                  Uw digitale klantomgeving voor elektrotechniek en installatie
                </h2>
                <div className="mt-4 space-y-4 text-base text-text-secondary">
                  <p>
                    Het klantportaal van Elvion Group Nederland is de centrale
                    omgeving voor iedereen die met ons samenwerkt aan
                    elektrotechnische installaties, laadpalen,
                    groepenkasten, camerabewaking en toegangscontrole. U
                    vindt hier alle documentatie rondom uw project op
                    één plek: getekende offertes, opdrachtbevestigingen,
                    facturen, opleverrapporten, NEN 1010-metingen,
                    schema&apos;s en garantiebewijzen.
                  </p>
                  <p>
                    Als zakelijke klant beheert u meerdere vestigingen,
                    afdelingen en contactpersonen binnen dezelfde
                    omgeving. Particuliere klanten houden onderhoud aan
                    hun laadpaal, meterkast of beveiligingsinstallatie
                    overzichtelijk bij en kunnen serviceverzoeken direct
                    doorzetten naar de servicedesk. Alle communicatie
                    rondom een project blijft gekoppeld aan uw dossier,
                    zodat u en onze monteurs altijd met dezelfde
                    informatie werken.
                  </p>
                  <p>
                    De omgeving is toegankelijk via computer, tablet en
                    smartphone. Toegang wordt beveiligd met een
                    persoonlijk wachtwoord en optionele
                    tweestapsverificatie. Uw gegevens worden opgeslagen
                    binnen de Europese Unie en verwerkt conform de
                    Algemene Verordening Gegevensbescherming.
                  </p>
                </div>

                <h3 className="mt-10 font-display text-2xl font-bold text-foreground">
                  Wat regelt u in het klantportaal?
                </h3>
                <ul className="mt-4 grid gap-3 text-sm text-text-secondary sm:grid-cols-2">
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Offertes online inzien en digitaal akkoord geven</li>
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Facturen downloaden als PDF per project of jaar</li>
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Servicemeldingen indienen en de status volgen</li>
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Planning en aanrijtijd van uw monteur bekijken</li>
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Opleverrapporten en meetstaten bewaren</li>
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Contactpersonen en factuuradressen beheren</li>
                </ul>

                <h3 className="mt-10 font-display text-2xl font-bold text-foreground">
                  Veelgestelde vragen
                </h3>
                <div className="mt-4 space-y-5 text-sm text-text-secondary">
                  <div>
                    <p className="font-semibold text-foreground">Voor wie is het klantportaal bedoeld?</p>
                    <p className="mt-1">Voor alle particuliere en zakelijke klanten van Elvion Group Nederland met een lopende offerte, opdracht of servicecontract.</p>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Hoe krijg ik toegang?</p>
                    <p className="mt-1">U maakt een account aan via de knop hierboven. Bestaande klanten ontvangen een uitnodiging per e-mail zodra hun eerste dossier is aangemaakt.</p>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Is de omgeving veilig?</p>
                    <p className="mt-1">Ja. Wij gebruiken versleutelde verbindingen, tweestapsverificatie en dataopslag binnen de EU volgens de AVG.</p>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Kan ik een servicemelding indienen buiten kantoortijden?</p>
                    <p className="mt-1">Ja. Meldingen kunt u 24 uur per dag doorgeven via het portaal. Onze servicedesk pakt uw verzoek op tijdens werkdagen.</p>
                  </div>
                </div>
              </div>

              <aside className="rounded-2xl border border-border bg-background p-6">
                <h3 className="font-display text-lg font-bold text-foreground">Waarom Elvion Group Nederland u helpt</h3>
                <ul className="mt-4 space-y-3 text-sm text-text-secondary">
                  <li className="flex gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Erkend installatiebedrijf, werkzaam in heel Nederland</li>
                  <li className="flex gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Gecertificeerde monteurs voor laadpalen en beveiliging</li>
                  <li className="flex gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Werkzaamheden conform NEN 1010 en NEN 3140</li>
                  <li className="flex gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Documentatie en garanties digitaal beschikbaar</li>
                </ul>
              </aside>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
